import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldCheck, KeyRound, LogOut, Trash2, Crown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';

export default function Profile() {
  const { user, access, logout } = useAuth();
  const navigate = useNavigate();
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState('');

  const changePw = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error('New password must be at least 8 characters');
    setBusy(true);
    try {
      await api.post('/auth/change-password', { current_password: cur, new_password: next });
      setCur('');
      setNext('');
      toast.success('Password updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not change password');
    } finally {
      setBusy(false);
    }
  };

  const doLogout = async () => {
    await logout();
    navigate('/');
  };

  const deleteAccount = async () => {
    if (confirm !== 'DELETE') return toast.error('Type DELETE to confirm');
    try {
      await api.delete('/auth/me', { data: { confirmation: 'DELETE' } });
      toast.success('Account deleted');
      await logout();
      navigate('/');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete account');
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-8" data-testid="profile-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-white">Profile</h1>
        <p className="mt-1 text-sm text-zinc-500">Manage your account and access.</p>
      </div>

      <Card className="p-8">
        <div className="flex items-center gap-5">
          <div className="grid h-20 w-20 place-items-center overflow-hidden rounded-2xl bg-ink-elevated text-2xl font-heading font-bold text-white">
            {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user?.name || 'U').charAt(0).toUpperCase()}
          </div>
          <div className="flex-1">
            <div className="font-heading text-2xl font-bold text-white">{user?.name}</div>
            <div className="text-sm text-zinc-500">{user?.email}</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {user?.is_admin && <Badge tone="emerald"><ShieldCheck size={12} /> Admin</Badge>}
              {access ? <Badge tone="amber"><Crown size={12} /> Full access</Badge> : <Badge tone="zinc">No entitlement</Badge>}
              {user?.subscription_status && <Badge tone="violet">{user.subscription_status}</Badge>}
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="mb-3 flex items-center gap-2 font-heading font-semibold text-white">
          <Crown size={18} className="text-emerald" /> Membership
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-white/5 bg-ink-surface p-4">
            <div className="text-xs text-zinc-500">Access</div>
            <div className="mt-1 font-medium text-white">{access ? 'Full access' : 'No active entitlement'}</div>
          </div>
          <div className="rounded-xl border border-white/5 bg-ink-surface p-4">
            <div className="text-xs text-zinc-500">Subscription</div>
            <div className="mt-1 font-medium text-white">{user?.subscription_status || (user?.is_premium ? 'Premium' : '—')}{user?.subscription_plan ? ` · ${user.subscription_plan}` : ''}</div>
          </div>
        </div>
        <p className="mt-3 text-xs text-zinc-500">Membership & billing are managed through Whop. Access is verified by the backend.</p>
      </Card>

      <Card className="p-6">
        <div className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold text-white">
          <KeyRound size={18} className="text-emerald" /> Change password
        </div>
        <form onSubmit={changePw} className="space-y-3">
          <Input type="password" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} required data-testid="cur-password-input" />
          <Input type="password" placeholder="New password (min 8 chars)" value={next} onChange={(e) => setNext(e.target.value)} required data-testid="new-password-input" />
          <Button type="submit" disabled={busy} data-testid="change-password-btn">
            {busy ? 'Updating…' : 'Update password'}
          </Button>
        </form>
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-heading font-semibold text-white">Sign out</div>
            <div className="text-sm text-zinc-500">End your session on this device.</div>
          </div>
          <Button variant="outline" onClick={doLogout} data-testid="profile-logout-btn">
            <LogOut size={16} /> Sign out
          </Button>
        </div>
      </Card>

      {!user?.is_admin && (
        <Card className="border-red-500/20 p-6">
          <div className="mb-3 flex items-center gap-2 font-heading font-semibold text-red-400">
            <Trash2 size={18} /> Delete account
          </div>
          <p className="mb-3 text-sm text-zinc-400">
            This permanently deletes your app account and personal data. Your Whop membership is not affected.
          </p>
          <div className="flex gap-3">
            <Input placeholder='Type "DELETE"' value={confirm} onChange={(e) => setConfirm(e.target.value)} className="max-w-[180px]" data-testid="delete-confirm-input" />
            <Button variant="danger" onClick={deleteAccount} data-testid="delete-account-btn">
              Delete
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
